import { inject, injectable } from 'inversify';
import { NETWORK_OBSERVER_EVENTS } from '../../constants/events';
import { TOKEN } from '../../inversify.tokens';
import type { LDTransport } from '../../types/mediasoup';
import type { TransportDirection, TransportStatePayload } from '../../types/network';
import Network from './index';
import { Logger } from '../../types/common';
import { serializeError } from 'serialize-error';

const MAX_DISCONNECTS = 3;
const WINDOW_MS = 30_000;
const DEDUP_MS = 400;
const TRANSPORT_DIRECTIONS: TransportDirection[] = ['send', 'recv'];

@injectable()
class NetworkTransportPolicyManager {
  #isMonitoring = false;

  readonly #disconnectTimestampByDirection = new Map<TransportDirection, number[]>();

  readonly #relayedTransportDirections = new Set<TransportDirection>();

  readonly #relayEscalationInFlightByDirection = new Map<TransportDirection, Promise<void>>();

  readonly #logger: Logger;

  constructor(
    @inject(TOKEN.Network) private readonly network: Network,
    @inject(TOKEN.LoggerFactory) private readonly loggerFactory: (namespace: string) => Logger,
  ) {
    this.#logger = this.loggerFactory('NetworkTransportPolicyManager');
  }

  startMonitoring(): void {
    if (this.#isMonitoring) {
      return;
    }

    this.refreshDirectionRelayState();
    this.#disconnectTimestampByDirection.clear();
    this.#isMonitoring = true;
    this.network.observer.on(NETWORK_OBSERVER_EVENTS.transportState, this.handleTransportState);
  }

  stopMonitoring(): void {
    if (!this.#isMonitoring) {
      return;
    }

    this.network.observer.off(NETWORK_OBSERVER_EVENTS.transportState, this.handleTransportState);
    this.#isMonitoring = false;
    this.#disconnectTimestampByDirection.clear();
  }

  async changeIceTransportPolicy(policy: RTCIceTransportPolicy): Promise<void> {
    await Promise.all(this.#relayEscalationInFlightByDirection.values());

    await Promise.all(TRANSPORT_DIRECTIONS.map(async (direction) => await this.applyRelayPolicy(direction, policy)));
    this.#disconnectTimestampByDirection.clear();

    if (policy === 'all') {
      this.#relayedTransportDirections.clear();

      return;
    }

    this.stopMonitoring();
  }

  private readonly handleTransportState = ({ state, direction }: TransportStatePayload): void => {
    if (!this.#isMonitoring) {
      return;
    }

    if (this.#relayedTransportDirections.has(direction)) {
      return;
    }

    if (this.#relayEscalationInFlightByDirection.has(direction)) {
      return;
    }

    if (!['disconnected', 'failed'].includes(state)) {
      return;
    }

    const disconnectCount = this.recordDisconnectAndGetCount(direction);

    if (!disconnectCount) {
      return;
    }

    if (disconnectCount <= MAX_DISCONNECTS) {
      return;
    }

    this.#logger.info('transport instability detected. Escalating to relay', {
      direction,
      disconnectCount,
    });

    this.escalateDirectionToRelay(direction)
      .catch((error: unknown) => {
        this.#logger.warn('Failed to apply relay policy', {
          direction,
          error: serializeError(error),
        });
      });
  };

  private recordDisconnectAndGetCount(direction: TransportDirection): number | undefined {
    const now = Date.now();
    const timestamps = this.#disconnectTimestampByDirection.get(direction) ?? [];
    const lastDisconnectAt = timestamps[timestamps.length - 1];

    if (NetworkTransportPolicyManager.isDuplicateDisconnect(lastDisconnectAt, now)) {
      return undefined;
    }

    const disconnectsInWindow = NetworkTransportPolicyManager.getDisconnectsInWindow(timestamps, now);
    disconnectsInWindow.push(now);
    this.#disconnectTimestampByDirection.set(direction, disconnectsInWindow);

    return disconnectsInWindow.length;
  }

  private async applyRelayPolicy(direction: TransportDirection, policy: RTCIceTransportPolicy): Promise<void> {
    const transport = this.getTransportByDirection(direction);

    if (!transport) {
      return;
    }

    const peerConnection = NetworkTransportPolicyManager.getPeerConnection(transport);

    if (!peerConnection) {
      return;
    }

    peerConnection.setConfiguration({
      ...peerConnection.getConfiguration(),
      iceTransportPolicy: policy,
    });

    await this.network.restartIce(transport);

    if (policy === 'relay') {
      this.#relayedTransportDirections.add(direction);
    } else {
      this.#relayedTransportDirections.delete(direction);
    }
  }

  public async escalateDirectionToRelay(direction: TransportDirection): Promise<void> {
    if (this.#relayedTransportDirections.has(direction)) {
      return;
    }

    const inFlightPromise = this.#relayEscalationInFlightByDirection.get(direction);

    if (inFlightPromise) {
      await inFlightPromise;

      return;
    }

    const escalationPromise = (async (): Promise<void> => {
      await this.applyRelayPolicy(direction, 'relay');
      this.#disconnectTimestampByDirection.delete(direction);
    })();

    this.#relayEscalationInFlightByDirection.set(direction, escalationPromise);

    try {
      await escalationPromise;
    } finally {
      this.#relayEscalationInFlightByDirection.delete(direction);
    }
  }

  private refreshDirectionRelayState(): void {
    TRANSPORT_DIRECTIONS.forEach((direction) => {
      const transport = this.getTransportByDirection(direction);
      const pc = NetworkTransportPolicyManager.getPeerConnection(transport);
      const policy = pc?.getConfiguration().iceTransportPolicy;

      if (policy === 'relay') {
        this.#relayedTransportDirections.add(direction);

        return;
      }

      this.#relayedTransportDirections.delete(direction);
    });
  }

  private getTransportByDirection(direction: TransportDirection): LDTransport | undefined {
    return direction === 'send'
      ? this.network.sendTransport
      : this.network.receiveTransport;
  }

  private static getPeerConnection(transport?: LDTransport): RTCPeerConnection | undefined {
    return transport?.handler?._pc;
  }

  private static getDisconnectsInWindow(timestamps: number[], now: number): number[] {
    const windowStart = now - WINDOW_MS;

    return timestamps.filter((timestamp) => timestamp >= windowStart);
  }

  private static isDuplicateDisconnect(lastDisconnectAt: number | undefined, now: number): boolean {
    return typeof lastDisconnectAt === 'number' && now - lastDisconnectAt < DEDUP_MS;
  }
}

export default NetworkTransportPolicyManager;
